import axios, { AxiosInstance } from 'axios';
import config from '../config/api';
import {
  ApiResponse,
  CloseStrategyPositionResponse,
  LatestCloseResponse,
  MarketAssetClass,
  OptionContractLookupRow,
  OptionContractSummary,
  OrderPreview,
  PlaceOrderRequest,
  PrevCloseResponse,
  PositionStateSnapshot,
  RiskLimitsResponse,
  ScheduleStrategyRequest,
  ScheduleStrategyResponse,
  TradingOrder,
  TradingOrderBlotterRow,
} from '../types/trading';

class TradingService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${config.tradingApiUrl}/trading`,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  private authHeaders(userUuid: string, token?: string) {
    return {
      'x-user-uuid': userUuid,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  async previewOrder(
    userUuid: string,
    request: PlaceOrderRequest,
    token?: string,
  ): Promise<OrderPreview> {
    const response = await this.client.post<ApiResponse<OrderPreview>>('/orders/preview', request, {
      headers: this.authHeaders(userUuid, token),
    });
    return response.data.data;
  }

  async placeOrder(
    userUuid: string,
    request: PlaceOrderRequest,
    token?: string,
  ): Promise<TradingOrder> {
    const response = await this.client.post<ApiResponse<TradingOrder>>('/orders', request, {
      headers: this.authHeaders(userUuid, token),
    });
    return response.data.data;
  }

  async cancelOrder(userUuid: string, orderId: string, token?: string): Promise<TradingOrder> {
    const response = await this.client.post<ApiResponse<TradingOrder>>(
      `/orders/${orderId}/cancel`,
      {},
      {
        headers: this.authHeaders(userUuid, token),
      },
    );
    return response.data.data;
  }

  async listOrders(userUuid: string, token?: string, limit = 200): Promise<TradingOrder[]> {
    const response = await this.client.get<ApiResponse<TradingOrder[]>>('/orders', {
      headers: this.authHeaders(userUuid, token),
      params: { limit },
    });
    return Array.isArray(response.data.data) ? response.data.data : [];
  }

  async listOrderBlotter(
    userUuid: string,
    token?: string,
    limit = 200,
  ): Promise<TradingOrderBlotterRow[]> {
    const response = await this.client.get<ApiResponse<TradingOrderBlotterRow[]>>('/order-blotter', {
      headers: this.authHeaders(userUuid, token),
      params: { limit },
    });
    return Array.isArray(response.data.data) ? response.data.data : [];
  }

  async getPositionState(userUuid: string, token?: string): Promise<PositionStateSnapshot> {
    return this.getPositionStateWithOptions(userUuid, token);
  }

  async getPositionStateWithOptions(
    userUuid: string,
    token?: string,
    options?: { includeClosed?: boolean },
  ): Promise<PositionStateSnapshot> {
    const includeClosed = options?.includeClosed === true;
    const response = await this.client.get<ApiResponse<PositionStateSnapshot>>('/position', {
      headers: this.authHeaders(userUuid, token),
      params: includeClosed ? { include_closed: true } : undefined,
    });
    return response.data.data;
  }

  async scheduleStrategy(
    userUuid: string,
    request: ScheduleStrategyRequest,
    token?: string,
  ): Promise<ScheduleStrategyResponse> {
    const response = await this.client.post<ApiResponse<ScheduleStrategyResponse>>(
      '/position/strategy/schedule',
      request,
      {
        headers: this.authHeaders(userUuid, token),
      },
    );
    return response.data.data;
  }

  async closeStrategyPosition(
    userUuid: string,
    strategyPositionId: string,
    token?: string,
  ): Promise<CloseStrategyPositionResponse> {
    const response = await this.client.post<ApiResponse<CloseStrategyPositionResponse>>(
      `/position/strategy/${strategyPositionId}/close`,
      {},
      {
        headers: this.authHeaders(userUuid, token),
      },
    );
    return response.data.data;
  }

  async getRiskLimits(userUuid: string, token?: string): Promise<RiskLimitsResponse> {
    const response = await this.client.get<ApiResponse<RiskLimitsResponse>>('/risk-limits', {
      headers: this.authHeaders(userUuid, token),
    });
    return response.data.data;
  }

  async listTopOptionContracts(underlying: string, limit = 10): Promise<OptionContractSummary[]> {
    const response = await this.client.get<ApiResponse<OptionContractSummary[]>>(
      '/market/options/contracts/top',
      {
        params: {
          underlying: underlying.trim().toUpperCase(),
          limit,
        },
      },
    );
    return Array.isArray(response.data.data) ? response.data.data : [];
  }

  async listOptionContractsByType(
    underlying: string,
    optionType: 'call' | 'put',
  ): Promise<OptionContractLookupRow[]> {
    const response = await this.client.get<ApiResponse<OptionContractLookupRow[]>>(
      '/market/options/contracts/by-type',
      {
        params: {
          underlying: underlying.trim().toUpperCase(),
          option_type: optionType,
        },
      },
    );
    return Array.isArray(response.data.data) ? response.data.data : [];
  }

  async listOptionContractsByStrike(
    underlying: string,
    optionType: 'call' | 'put',
    strike: number,
  ): Promise<OptionContractLookupRow[]> {
    const response = await this.client.get<ApiResponse<OptionContractLookupRow[]>>(
      '/market/options/contracts/by-strike',
      {
        params: {
          underlying: underlying.trim().toUpperCase(),
          option_type: optionType,
          strike,
        },
      },
    );
    return Array.isArray(response.data.data) ? response.data.data : [];
  }

  async listOptionContractsByExpiration(
    underlying: string,
    optionType: 'call' | 'put',
    expiration: string,
  ): Promise<OptionContractLookupRow[]> {
    const response = await this.client.get<ApiResponse<OptionContractLookupRow[]>>(
      '/market/options/contracts/by-expiration',
      {
        params: {
          underlying: underlying.trim().toUpperCase(),
          option_type: optionType,
          expiration,
        },
      },
    );
    return Array.isArray(response.data.data) ? response.data.data : [];
  }

  async getPrevClose(
    assetClass: MarketAssetClass,
    symbols: string[],
  ): Promise<PrevCloseResponse> {
    const normalizedSymbols = Array.from(
      new Set(
        symbols
          .map((symbol) => symbol.trim().toUpperCase())
          .filter((symbol) => symbol.length > 0),
      ),
    );

    const response = await this.client.get<ApiResponse<PrevCloseResponse>>(
      '/market/prev-close',
      {
        params: {
          asset_class: assetClass,
          symbols: normalizedSymbols.join(','),
        },
      },
    );
    return response.data.data;
  }

  async getLatestClose(
    assetClass: MarketAssetClass,
    symbols: string[],
  ): Promise<LatestCloseResponse> {
    const normalizedSymbols = Array.from(
      new Set(
        symbols
          .map((symbol) => symbol.trim().toUpperCase())
          .filter((symbol) => symbol.length > 0),
      ),
    );

    const response = await this.client.get<ApiResponse<LatestCloseResponse>>(
      '/market/latest-close',
      {
        params: {
          asset_class: assetClass,
          symbols: normalizedSymbols.join(','),
        },
      },
    );
    return response.data.data;
  }
}

export const tradingService = new TradingService();
